class VoyageOverview extends HTMLElement {
  connectedCallback() {
    this.layout = this.querySelector('.voyage-overview__layout');
    this.toggles = this.querySelectorAll('[data-voyage-map-toggle]');
    if (!this.layout || !this.toggles.length) return;

    this.mapContainer = this.querySelector('[data-voyage-map]');
    this.map = null;
    this.markers = [];
    this.markerStyle = this.getAttribute('data-marker-style') || 'price';
    this.i18nAll = this.getAttribute('data-i18n-all') || 'Tous';
    this.i18nFromPrice = this.getAttribute('data-i18n-from-price') || 'À partir de';

    this.voyages = this.getVoyages();
    this.cards = Array.from(this.querySelectorAll('.voyage-overview__card-item'));
    this.categoryPills = Array.from(this.querySelectorAll('[data-category]'));
    this.geoContainer = this.querySelector('[data-voyage-geo]');
    this.grid = this.querySelector('.voyage-overview__grid');
    this.activeCategory = 'all';
    this.activeZone = 'all';

    // Progressive reveal: show the first batch, grow as the sentinel scrolls into view.
    this.batchSize = 12;
    this.visibleLimit = this.batchSize;
    this.matchingCount = 0;

    this.buildGeoPills();
    this.#hideUnusedCategoryPills();

    const params = new URLSearchParams(window.location.search);
    const initial = params.get('map') === 'true' ? 'map' : this.layout.getAttribute('data-view') || 'list';
    this.setView(initial, false);

    this.toggles.forEach((button) => {
      button.addEventListener('click', () => {
        const next = this.layout.getAttribute('data-view') === 'map' ? 'list' : 'map';
        this.setView(next, true);
      });
    });

    this.categoryPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        this.activeCategory = pill.getAttribute('data-category') || 'all';
        this.categoryPills.forEach((other) => {
          const active = other === pill;
          other.classList.toggle('voyage-overview__filter-tag--active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.visibleLimit = this.batchSize;
        this.updateGeoPills();
        this.applyFilters();
      });
    });

    const resetBtn = this.querySelector('[data-voyage-reset]');
    if (resetBtn) resetBtn.addEventListener('click', () => this.resetFilters());

    this.#setupReveal();
    this.applyFilters();
  }

  disconnectedCallback() {
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.maplibreTimer) clearInterval(this.maplibreTimer);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  /** Append a sentinel after the grid and grow the reveal limit as it scrolls into view. */
  #setupReveal() {
    if (!('IntersectionObserver' in window)) {
      // No observer support: reveal everything rather than stranding cards past the first batch.
      this.visibleLimit = this.cards.length;
      return;
    }
    if (!this.grid) return;
    this.sentinel = document.createElement('li');
    this.sentinel.className = 'voyage-overview__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.grid.appendChild(this.sentinel);

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && this.matchingCount > this.visibleLimit) {
          this.visibleLimit += this.batchSize;
          this.applyFilters();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this.revealObserver.observe(this.sentinel);
  }

  /** True when a voyage matches the active category. */
  matchesCategory(categories) {
    if (this.activeCategory === 'all') return true;
    return (categories || '').split(',').includes(this.activeCategory);
  }

  /** True when a voyage belongs to the active geo zone (a voyage may span several). */
  matchesZone(zones) {
    if (this.activeZone === 'all') return true;
    return (zones || '').split('|').includes(this.activeZone);
  }

  /**
   * Show/hide cards and markers against category AND zone.
   * Cards additionally respect the progressive-reveal limit (first N matches);
   * markers always show every match — the limit is a grid-only concern (RM-18).
   */
  applyFilters() {
    let matching = 0;
    this.cards.forEach((card) => {
      const ok = this.matchesCategory(card.getAttribute('data-categories')) && this.matchesZone(card.getAttribute('data-zones'));
      if (!ok) {
        card.hidden = true;
        return;
      }
      matching += 1;
      card.hidden = matching > this.visibleLimit;
    });

    this.matchingCount = matching;
    this.layout.setAttribute('data-empty', matching === 0 ? 'true' : 'false');

    this.markers.forEach(({ element, voyage }) => {
      const ok = this.matchesCategory(voyage.categories) && this.matchesZone(voyage.zones);
      element.style.display = ok ? '' : 'none';
    });
  }

  /** Clear both filter groups back to Tous (from the empty-state CTA). */
  resetFilters() {
    this.activeCategory = 'all';
    this.activeZone = 'all';
    this.visibleLimit = this.batchSize;
    this.categoryPills.forEach((pill) => {
      const active = pill.getAttribute('data-category') === 'all';
      pill.classList.toggle('voyage-overview__filter-tag--active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    this.updateGeoPills();
    this.applyFilters();
  }

  /** Hide category pills no voyage uses — categories are global metaobjects, so some may be unused. */
  #hideUnusedCategoryPills() {
    const used = new Set();
    this.voyages.forEach((voyage) => {
      (voyage.categories || '').split(',').forEach((cat) => cat && used.add(cat));
    });
    this.categoryPills.forEach((pill) => {
      const handle = pill.getAttribute('data-category');
      if (handle === 'all') return;
      const item = pill.closest('li') || pill;
      item.hidden = !used.has(handle);
    });
  }

  /** Render the geo pills once from the dataset zones (Tous + one per zone). */
  buildGeoPills() {
    if (!this.geoContainer) return;

    const zones = [];
    this.voyages.forEach((voyage) => {
      (voyage.zones || '').split('|').forEach((zone) => {
        if (zone && !zones.includes(zone)) zones.push(zone);
      });
    });

    const makePill = (zone, label) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'voyage-overview__geo-pill';
      pill.setAttribute('data-zone', zone);
      pill.setAttribute('aria-pressed', 'false');
      const text = document.createElement('span');
      text.className = 'voyage-overview__geo-label';
      text.textContent = label;
      const count = document.createElement('span');
      count.className = 'voyage-overview__geo-count';
      pill.append(text, count);
      pill.addEventListener('click', () => {
        this.activeZone = zone;
        this.visibleLimit = this.batchSize;
        this.updateGeoPills();
        this.applyFilters();
      });
      return pill;
    };

    this.geoContainer.append(makePill('all', this.i18nAll), ...zones.map((zone) => makePill(zone, zone)));
    this.updateGeoPills();
  }

  /** Refresh geo pill counts, active state, and zero-count visibility against the active category. */
  updateGeoPills() {
    if (!this.geoContainer) return;

    const pills = Array.from(this.geoContainer.querySelectorAll('.voyage-overview__geo-pill'));

    // First pass — counts and zero-count visibility, collapsing selection if the active zone vanished.
    pills.forEach((pill) => {
      const zone = pill.getAttribute('data-zone');
      const count = this.voyages.filter((voyage) => this.matchesCategory(voyage.categories) && (zone === 'all' || (voyage.zones || '').split('|').includes(zone))).length;
      pill.querySelector('.voyage-overview__geo-count').textContent = count;

      const hidden = zone !== 'all' && count === 0;
      pill.hidden = hidden;
      if (hidden && this.activeZone === zone) this.activeZone = 'all';
    });

    // Second pass — active state, now that activeZone is settled.
    pills.forEach((pill) => {
      const active = pill.getAttribute('data-zone') === this.activeZone;
      pill.classList.toggle('voyage-overview__geo-pill--active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  setView(view, updateUrl) {
    this.layout.setAttribute('data-view', view);
    this.setAttribute('data-view', view);
    this.toggles.forEach((button) => {
      button.setAttribute('aria-pressed', view === 'map' ? 'true' : 'false');
    });
    if (updateUrl) {
      const url = new URL(window.location.href);
      if (view === 'map') {
        url.searchParams.set('map', 'true');
      } else {
        url.searchParams.delete('map');
      }
      window.history.replaceState({}, '', url);
    }

    if (view === 'map') {
      // The map container has no size while hidden, so init/resize only once it is shown.
      this.ensureMap();
    } else if (this.activeZone !== 'all') {
      // Geo selection is map-only; reset it when leaving the map (spec F3).
      this.activeZone = 'all';
      this.visibleLimit = this.batchSize;
      this.updateGeoPills();
      this.applyFilters();
    }
  }

  /** Read the voyage dataset emitted by snippets/voyage-fixtures.liquid. */
  getVoyages() {
    const node = this.querySelector('#voyage-fixtures-json');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  /** Lazily load MapLibre (deferred CDN script) then build the map. */
  async ensureMap() {
    if (this.map) {
      this.map.resize();
      return;
    }
    if (!this.mapContainer) return;

    const maplibre = await this.#whenMaplibreReady();
    if (!maplibre || this.map) return;

    const voyages = this.voyages.filter((v) => v.latitude != null && v.longitude != null);

    this.map = new maplibre.Map({
      container: this.mapContainer,
      style: {
        version: 8,
        sources: {
          osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
          },
        },
        layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
      },
      center: [2.4, 46.6],
      zoom: 4,
      attributionControl: { compact: true },
    });

    this.map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-right');

    const placeholder = this.mapContainer.querySelector('.voyage-overview__map-placeholder');
    if (placeholder) placeholder.remove();

    this.map.on('load', () => {
      if (!voyages.length) return;
      const bounds = new maplibre.LngLatBounds();
      voyages.forEach((voyage) => {
        const lngLat = [voyage.longitude, voyage.latitude];
        const usePins = this.markerStyle === 'pins';
        const element = usePins ? this.#buildMarker(voyage) : this.#buildPricePill(voyage);
        const marker = new maplibre.Marker({ element }).setLngLat(lngLat);
        // Both marker styles open the same popup card on click; the card carries the link-through.
        // offset 20 = 16 base + 4px extra gap so the card sits clear of the marker.
        const popup = new maplibre.Popup({ offset: 20, closeButton: false, anchor: 'bottom', className: 'voyage-overview__popup-wrap' }).setDOMContent(this.#buildPopupCard(voyage));
        marker.setPopup(popup);
        marker.addTo(this.map);
        this.markers.push({ element, voyage });
        bounds.extend(lngLat);
      });
      this.map.fitBounds(bounds, { padding: 56, maxZoom: 9, duration: 0 });
      this.applyFilters();
    });
  }

  /** Navy dot marker (opens a popup card on click). */
  #buildMarker(voyage) {
    const marker = document.createElement('button');
    marker.type = 'button';
    marker.className = 'voyage-overview__marker';
    marker.setAttribute('aria-label', voyage.title);
    return marker;
  }

  /** Price-pill marker; opens the shared popup card on click (the marker's popup), which carries the link. */
  #buildPricePill(voyage) {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'voyage-overview__price-pin';
    pill.textContent = voyage.price_amount > 0 ? voyage.price : voyage.title;
    pill.setAttribute('aria-label', voyage.title);
    return pill;
  }

  /** Popup card: image, name, location, from-price — links through with the same typology routing as the grid card. */
  #buildPopupCard(voyage) {
    const href = voyage.typology === 'verychic' ? voyage.lien_externe : voyage.url;

    const card = document.createElement('a');
    card.className = 'voyage-overview__popup';
    card.href = href || '#';
    if (voyage.typology === 'verychic') {
      card.target = '_blank';
      card.rel = 'noopener';
    }

    const media = document.createElement('span');
    media.className = 'voyage-overview__popup-media';
    if (voyage.image) media.style.backgroundImage = `url(${voyage.image})`;

    const info = document.createElement('span');
    info.className = 'voyage-overview__popup-info';

    const loc = document.createElement('span');
    loc.className = 'voyage-overview__popup-loc';
    const cityEl = document.createElement('span');
    cityEl.className = 'voyage-overview__popup-city';
    cityEl.textContent = voyage.title;
    loc.appendChild(cityEl);
    if (voyage.ville_pays) {
      const countryEl = document.createElement('span');
      countryEl.className = 'voyage-overview__popup-country';
      countryEl.textContent = voyage.ville_pays;
      loc.appendChild(countryEl);
    }

    info.append(loc);
    if (voyage.price_amount > 0) {
      const price = document.createElement('span');
      price.className = 'voyage-overview__popup-price';
      const priceLabel = document.createElement('span');
      priceLabel.className = 'voyage-overview__popup-price-label';
      priceLabel.textContent = this.i18nFromPrice;
      const priceValue = document.createElement('span');
      priceValue.className = 'voyage-overview__popup-price-value';
      priceValue.textContent = voyage.price;
      price.append(priceLabel, priceValue);
      info.append(price);
    }
    card.append(media, info);
    return card;
  }

  #whenMaplibreReady() {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    return new Promise((resolve) => {
      let waited = 0;
      this.maplibreTimer = setInterval(() => {
        waited += 50;
        if (window.maplibregl) {
          clearInterval(this.maplibreTimer);
          resolve(window.maplibregl);
        } else if (waited >= 5000) {
          clearInterval(this.maplibreTimer);
          resolve(null);
        }
      }, 50);
    });
  }
}

if (!customElements.get('voyage-overview')) {
  customElements.define('voyage-overview', VoyageOverview);
}
